"use client";

import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { sessionCacheManager } from "../../lib/session/session-cache-manager";
import { sessionRequestManager } from "../../lib/session/session-request-manager";
import { sessionCacheInvalidation } from "../../lib/session/session-cache-invalidation";
import { sessionEventSystem } from "../../lib/session/session-event-system";
import logger from "logger";

export interface SessionContextValue {
    session: any | null;
    isLoading: boolean;
    error: Error | null;
    refresh: () => Promise<void>;
    lastUpdated: Date;
}

export interface SessionProviderProps {
    children: React.ReactNode;
    refreshInterval?: number; // Default: 5 minutes
    maxRetries?: number; // Default: 3
    // Test-only opt-in to legacy initial loading behavior
    showInitialLoading?: boolean;
}

const SessionContext = createContext<SessionContextValue | null>(null);

export function SessionProvider({
    children,
    refreshInterval = 5 * 60 * 1000, // 5 minutes
    maxRetries: _maxRetries = 3,
    showInitialLoading = false,
}: SessionProviderProps) {
    const [session, setSession] = useState<any | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<Error | null>(null);
    const [lastUpdated, setLastUpdated] = useState<Date>(new Date(0));

    const refreshIntervalRef = useRef<NodeJS.Timeout | null>(null);
    const mountedRef = useRef(true);
    const eventUnsubscribers = useRef<(() => void)[]>([]);

    const loadSession = useCallback(async (force = false) => {
        try {
            setError(null);

            // Check cache first unless forced refresh
            if (!force) {
                const cachedSession = sessionCacheManager.get();
                if (cachedSession !== null) {
                    setSession(cachedSession);
                    setLastUpdated(sessionCacheManager.getLastUpdated());
                    return;
                }
            }

            // Optimistic access by default; optional test-only initial loading
            if (force || (showInitialLoading && session === null)) {
                setIsLoading(true);
            }

            const freshSession = await sessionRequestManager.getSession();

            if (!mountedRef.current) return;

            // Update cache and state
            sessionCacheManager.set(freshSession);
            setSession(freshSession);
            setLastUpdated(new Date());

            // Emit session refresh event
            sessionEventSystem.emit('session-refreshed', {
                session: freshSession,
                forced: force,
            });

        } catch (err) {
            if (!mountedRef.current) return;

            const error = err as Error;
            setError(error);
            logger.error("Failed to load session:", error);
        } finally {
            if (mountedRef.current) {
                setIsLoading(false);
            }
        }
    }, [session]);

    const refresh = useCallback(async () => {
        sessionCacheManager.invalidate('manual-refresh');
        await loadSession(true);
    }, [loadSession]);

    // Set up cache invalidation system
    useEffect(() => {
        // Configure invalidation system with refresh callback
        sessionCacheInvalidation.setRefreshCallback(async () => {
            if (mountedRef.current) {
                await loadSession(true);
            }
        });

        // Listen for cache invalidation events from other tabs
        const unsubscribeCacheInvalidated = sessionEventSystem.on('cache-invalidated', (event) => {
            if (event.source === 'storage' && mountedRef.current) {
                // Another tab invalidated the cache, refresh our session
                loadSession(true);
            }
        });

        // Listen for session change events from other tabs
        const unsubscribeSessionChanged = sessionEventSystem.on('session-changed', (event) => {
            if (event.source === 'storage' && mountedRef.current) {
                // Update our local state with the new session data
                setSession(event.data.newSession);
                setLastUpdated(new Date(event.timestamp));
            }
        });

        // Listen for auth state changes
        const unsubscribeAuthStateChanged = sessionEventSystem.on('auth-state-changed', (_event) => {
            if (mountedRef.current) {
                // Force refresh on auth state changes
                loadSession(true);
            }
        });

        eventUnsubscribers.current = [
            unsubscribeCacheInvalidated,
            unsubscribeSessionChanged,
            unsubscribeAuthStateChanged,
        ];

        return () => {
            eventUnsubscribers.current.forEach(unsubscribe => unsubscribe());
            eventUnsubscribers.current = [];
        };
    }, [loadSession]);

    // Initial session load
    useEffect(() => {
        loadSession();
    }, [loadSession]);

    // Set up refresh interval with staleness checking
    useEffect(() => {
        if (refreshInterval > 0) {
            refreshIntervalRef.current = setInterval(async () => {
                // Check for staleness and refresh if needed
                const wasStale = await sessionCacheInvalidation.checkAndRefreshStale();

                // If not stale but cache is expired, refresh normally
                if (!wasStale && sessionCacheManager.isStale()) {
                    loadSession();
                }
            }, refreshInterval);

            return () => {
                if (refreshIntervalRef.current) {
                    clearInterval(refreshIntervalRef.current);
                }
            };
        }
    }, [refreshInterval, loadSession]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            mountedRef.current = false;
            sessionRequestManager.cancelPendingRequests();
            eventUnsubscribers.current.forEach(unsubscribe => unsubscribe());
            if (refreshIntervalRef.current) {
                clearInterval(refreshIntervalRef.current);
            }
        };
    }, []);

    const contextValue: SessionContextValue = {
        session,
        isLoading,
        error,
        refresh,
        lastUpdated,
    };

    return (
        <SessionContext.Provider value={contextValue}>
            {children}
        </SessionContext.Provider>
    );
}

export function useSessionContext(): SessionContextValue {
    const context = useContext(SessionContext);
    if (!context) {
        throw new Error("useSessionContext must be used within a SessionProvider");
    }
    return context;
}